/**
 * Policy Templates API Service
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============== Policy Template Categories ==============

export const getPolicyCategories = async () => {
  const response = await axios.get(`${API_URL}/api/policy-templates/categories`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Policy Templates (Read-only) ==============

export const getPolicyTemplates = async (params = {}) => {
  const response = await axios.get(`${API_URL}/api/policy-templates/templates`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getPolicyTemplate = async (templateId) => {
  const response = await axios.get(`${API_URL}/api/policy-templates/templates/${templateId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const previewPolicyTemplate = async (templateId, organizationName) => {
  const response = await axios.get(`${API_URL}/api/policy-templates/templates/${templateId}/preview`, {
    params: { organization_name: organizationName },
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Generated Policies ==============

export const getGeneratedPolicies = async (params = {}) => {
  const response = await axios.get(`${API_URL}/api/policy-templates/generated`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createGeneratedPolicy = async (data) => {
  const response = await axios.post(`${API_URL}/api/policy-templates/generated`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getGeneratedPolicy = async (policyId) => {
  const response = await axios.get(`${API_URL}/api/policy-templates/generated/${policyId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateGeneratedPolicy = async (policyId, data) => {
  const response = await axios.put(`${API_URL}/api/policy-templates/generated/${policyId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteGeneratedPolicy = async (policyId) => {
  const response = await axios.delete(`${API_URL}/api/policy-templates/generated/${policyId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const approveGeneratedPolicy = async (policyId, action, comment = '') => {
  const response = await axios.post(`${API_URL}/api/policy-templates/generated/${policyId}/approve`, 
    { action, comment },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const exportGeneratedPolicy = async (policyId, format = 'markdown') => {
  const response = await axios.get(`${API_URL}/api/policy-templates/generated/${policyId}/export`, {
    params: { format },
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Dashboard ==============

export const getPolicyDashboard = async () => {
  const response = await axios.get(`${API_URL}/api/policy-templates/dashboard`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Helper Functions ==============

export const getCategoryIcon = (categoryId) => {
  const icons = {
    governance: '🏛️',
    access_control: '🔑',
    asset_management: '🖥️',
    operations: '⚙️',
    communications: '🌐',
    development: '💻',
    supplier: '🤝',
    incident: '🚨',
    continuity: '🔄',
    compliance: '📋',
    hr: '👥',
    physical: '🛡️',
    data: '🗄️',
    cryptography: '🔐',
    risk: '📊'
  };
  return icons[categoryId] || '📄';
};

export const getStatusColor = (status) => {
  const colors = {
    draft: 'gray',
    pending_review: 'yellow',
    approved: 'green',
    rejected: 'red',
    published: 'blue'
  };
  return colors[status] || 'gray';
};

export const getStatusLabel = (status) => {
  const labels = {
    draft: 'Draft',
    pending_review: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    published: 'Published'
  };
  return labels[status] || status;
};

export const getFrameworkBadgeColor = (framework) => {
  const colors = {
    ISO27001: 'blue',
    NIST: 'purple',
    SOC2: 'green',
    GDPR: 'indigo',
    HIPAA: 'pink',
    'PCI-DSS': 'orange',
    CIS: 'teal',
    ISO31000: 'cyan'
  };
  return colors[framework] || 'gray';
};

// Download helper
export const downloadPolicy = async (policyId, format, filename) => {
  const data = await exportGeneratedPolicy(policyId, format);
  
  let blob;
  let extension;
  
  if (format === 'markdown') {
    blob = new Blob([data.content], { type: 'text/markdown' });
    extension = '.md';
  } else if (format === 'html') {
    blob = new Blob([data.content], { type: 'text/html' });
    extension = '.html';
  } else {
    blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    extension = '.json';
  }
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = (filename || 'policy') + extension;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default {
  getPolicyCategories,
  getPolicyTemplates,
  getPolicyTemplate,
  previewPolicyTemplate,
  getGeneratedPolicies,
  createGeneratedPolicy,
  getGeneratedPolicy,
  updateGeneratedPolicy,
  deleteGeneratedPolicy,
  approveGeneratedPolicy,
  exportGeneratedPolicy,
  getPolicyDashboard,
  getCategoryIcon,
  getStatusColor,
  getStatusLabel,
  getFrameworkBadgeColor,
  downloadPolicy
};
